import { IAuthService } from '@velociti/core'
import { AxiosError, AxiosInstance } from 'axios'
import { usePrimeVueToast } from 'src/lib/compose/usePrimeVueToast'
import Router from 'src/router'
import { useAuthStore } from 'src/stores/auth'
import ApiResult from 'src/system/auth/models/ApiResult'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    isRetryRequest?: boolean
  }
  interface AxiosRequestConfig {
    isRetryRequest?: boolean
  }
}

const excludedUrls = ['/api/user/auth']

interface QueueItem {
  resolve: (value: unknown) => void
  reject: (reason?: any) => void
}

let isRefreshing = false
let queue: Array<QueueItem> = []

const processQueue = (isOk = true, token: string | null = null) => {
  for (const prom of queue) {
    if (!isOk) {
      prom.reject()
    } else {
      prom.resolve(token)
    }
  }

  queue = []
}

const interceptor = (api: AxiosInstance, authService: IAuthService) => (error: AxiosError) => {
  const { config, response } = error

  if (!config || !config.url) {
    return Promise.reject(error)
  }

  if (excludedUrls.includes(config.url)) {
    return Promise.reject(error)
  }

  const originalRequest = config

  if (originalRequest && originalRequest.isRetryRequest) {
    return Promise.reject(error)
  }

  if (error !== undefined && error.response === undefined) {
    // An error without a response = network error, probably offline or a bad URL, so reject it
    return Promise.reject(error)
  }

  const status = response?.status

  if (status && status !== 401 && status !== 403) {
    return Promise.reject(error)
  }

  if (isRefreshing) {
    return new Promise(function (resolve, reject) {
      queue.push({ resolve, reject })
    })
      .then((token) => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`
        return api(originalRequest)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }

  originalRequest.isRetryRequest = true
  isRefreshing = true

  return new Promise((resolve, reject) => {
    return authService
      .refresh()
      .then((result: ApiResult) => {
        if (!result.status) {
          throw new Error(`Failed to refresh token: ${result.message}`)
        }

        originalRequest.headers['Authorization'] = `Bearer ${result.data?.access_token}`
        processQueue(true, result.data?.access_token)
        resolve(api(originalRequest))
      })
      .catch((err) => {
        usePrimeVueToast().add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to refresh the login token. Please sign in again.',
        })
        processQueue(false)
        useAuthStore().logout()
        Router()
          .then((router) => router.push({ name: 'login' }))
          .finally(() => reject(err))
      })
      .then(() => {
        isRefreshing = false
      })
  })
}

export default interceptor
